import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { CostSettingProfile } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

import styles from './delete-profile.module.css';

export interface SettingsCostModalsDeleteProfileSignature {
  Args: {
    onClose: () => void;
    onDeleted: () => void;
    profile: CostSettingProfile;
  };
}

export default class SettingsCostModalsDeleteProfile extends Component<SettingsCostModalsDeleteProfileSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  deleteTask = task({ drop: true }, async (): Promise<void> => {
    try {
      await this.args.profile.destroyRecord({
        adapterOptions: { namespace: APIGATEWAY, typeKey: 'cost-profiles' },
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });

      return;
    }

    this.toast.success(this.intl.t('settings.cost.deleted'), {
      showIcon: true,
    });
    this.args.onDeleted();
    this.args.onClose();
  });

  get ctaLabel(): string {
    return this.intl.t('settings.cost.delete_profile.cta');
  }

  get description(): string {
    return this.intl.t('settings.cost.delete_profile.description');
  }

  get title(): string {
    return this.intl.t('settings.cost.delete_profile.title');
  }

  @action handleDelete(): void {
    void this.deleteTask.perform();
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.deleteTask.isRunning}}
      @onClose={{@onClose}}
      class={{styles.confirmation-modal}}
      data-test-id="cost-delete-profile-modal"
    >
      <:default>
        <div class="flex flex-col items-center justify-center">
          <PrismMessage
            @description={{this.description}}
            @illustration="100x100_warning"
            @title={{this.title}}
          />
        </div>
      </:default>

      <:footer>
        <div class="m-300 flex justify-end gap-300">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.deleteTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-id="cost-delete-profile-cancel-button"
          />
          <PrismButton
            @buttonType="critical"
            @isLoading={{this.deleteTask.isRunning}}
            @label={{this.ctaLabel}}
            @onClick={{this.handleDelete}}
            data-test-id="cost-delete-profile-confirm-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
